<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Retraits Natcash</h1>
        <p class="page-subtitle">{{ results.length }} demande{{ results.length > 1 ? 's' : '' }} de retrait</p>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Filters -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="search"
                placeholder="Rechercher..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                :items="statuses"
                item-value="id"
                item-text="label"
                placeholder="Statut"
                v-model="filterStatus"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="auto">
              <div class="d-flex gap-2">
                <v-btn color="primary" depressed small @click="filter" class="mr-1">
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
                <v-btn outlined small @click="reset" class="mr-1">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="error" outlined small @click="exportPDF" class="mr-1">
                  <v-icon small>mdi-file-pdf-box</v-icon>
                </v-btn>
                <v-btn color="success" outlined small @click="exportExcel">
                  <v-icon small>mdi-file-excel</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table" id="content">
            <thead>
              <tr>
                <th class="text-left">TELEPHONE</th>
                <th>MONTANT</th>
                <th>FRAIS</th>
                <th>A RECEVOIR</th>
                <th>DATE</th>
                <th>HEURE</th>
                <th>STATUT</th>
                <th>PAIEMENT</th>
                <th></th>
              </tr>
              <tr class="totals-row">
                <th class="text-left">TOTAL ({{ filteredResults.length }} demandes)</th>
                <th>{{ formatCurrency(totalAmount) }}</th>
                <th>{{ formatCurrency(totalFees) }}</th>
                <th>{{ formatCurrency(totalReceive) }}</th>
                <th colspan="5"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredResults" :key="item.id">
                <td class="text-left">{{ item.phone }}</td>
                <td>{{ formatCurrency(parseFloat(item.amount) + parseFloat(item.fee_amount)) }}</td>
                <td>{{ formatCurrency(item.fee_amount) }}</td>
                <td>{{ formatCurrency(item.amount) }}</td>
                <td class="text-caption">{{ item.created_at | formatDate }}</td>
                <td class="text-caption">{{ item.created_at | formatTime }}</td>
                <td>
                  <v-chip x-small :color="getStatusChipColor(item.status)" :text-color="getStatusChipTextColor(item.status)">
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </td>
                <td>
                  <v-chip x-small :color="getStatusChipColor(item.partner_status)" :text-color="getStatusChipTextColor(item.partner_status)">
                    {{ getStatusText(item.partner_status) }}
                  </v-chip>
                </td>
                <td>
                  <v-btn icon x-small @click="view(item.id)">
                    <v-icon small color="secondary">mdi-eye-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="filteredResults.length === 0">
                <td colspan="9" class="text-center py-4 grey--text">Aucune demande de retrait</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import PreloaderComponent from '../../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../../components/BreadcrumbComponent.vue'

export default {
  name: 'WithdrawalRequestView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    search: '',
    results: [],
    enable: false,
    filterStatus: null,
    statuses: [
      { id: 'APPROVED', label: 'Approuve' },
      { id: 'PAID', label: 'Paye' },
      { id: 'PENDING', label: 'En attente' },
      { id: 'REJECTED', label: 'Rejete' },
    ],
    items: [
      { text: 'ACCUEIL', disabled: false, href: '/' },
      { text: 'NATCASH', disabled: false, href: '/natcash/balance' },
      { text: 'RETRAITS', disabled: true, href: '/natcash/withdrawal-requests' },
    ],
  }),
  computed: {
    filteredResults() {
      let data = this.results
      if (this.filterStatus) {
        data = data.filter(item => item.status === this.filterStatus)
      }
      if (this.search) {
        const s = this.search.toLowerCase()
        data = data.filter(item =>
          (item.phone && item.phone.toLowerCase().includes(s)) ||
          (item.amount && String(item.amount).includes(s)) ||
          (item.status && item.status.toLowerCase().includes(s))
        )
      }
      return data
    },
    totalAmount() {
      return this.filteredResults.reduce((sum, item) => sum + parseFloat(item.amount) + parseFloat(item.fee_amount), 0)
    },
    totalFees() {
      return this.filteredResults.reduce((sum, item) => sum + parseFloat(item.fee_amount), 0)
    },
    totalReceive() {
      return this.filteredResults.reduce((sum, item) => sum + parseFloat(item.amount), 0)
    },
  },
  methods: {
    formatCurrency(value) {
      const num = parseFloat(value)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    load() {
      this.enable = false
      let url = '/withdrawal-requests?processor_name=NATCASH&include=business,payments'
      if (this.$route.query.from) url += '&from=' + this.$route.query.from
      if (this.$route.query.to) url += '&to=' + this.$route.query.to

      const self = this
      this.$axios.get(url)
        .then(function (response) {
          self.results = response.data
          self.enable = true
        }).catch(function () {
          self.enable = true
        })
    },
    filter() {
      // filtering is reactive via computed
    },
    reset() {
      this.search = ''
      this.filterStatus = null
    },
    getStatusChipColor(status) {
      if (status === 'APPROVED' || status === 'PAID') return 'green lighten-4'
      if (status === 'PENDING') return 'blue lighten-4'
      return 'red lighten-4'
    },
    getStatusChipTextColor(status) {
      if (status === 'APPROVED' || status === 'PAID') return 'green darken-3'
      if (status === 'PENDING') return 'blue darken-3'
      return 'red darken-3'
    },
    getStatusText(status) {
      if (status === 'APPROVED') return 'Approuve'
      if (status === 'PAID') return 'Paye'
      if (status === 'PENDING') return 'En attente'
      if (status === 'CONFIRMED') return 'Confirme'
      return 'Rejete'
    },
    view(id) {
      this.$router.push('/natcash/withdrawal-requests/' + id)
    },
    async exportPDF() {
      const { jsPDF } = await import('jspdf')
      const biz = JSON.parse(localStorage.business || '{}')
      const doc = new jsPDF('l', 'mm', 'a4')
      const pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml

      // Header
      doc.setFillColor(25, 65, 115)
      doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(255, 255, 255)
      doc.text('RETRAITS NATCASH', ml, 11)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(200, 215, 235)
      if (biz.name) doc.text(biz.name, mr, 11, { align: 'right' })

      let y = 24
      const cols = [
        { label: 'TELEPHONE', w: 35, align: 'left' },
        { label: 'MONTANT', w: 30, align: 'right' },
        { label: 'FRAIS', w: 25, align: 'right' },
        { label: 'A RECEVOIR', w: 30, align: 'right' },
        { label: 'DATE', w: 28, align: 'center' },
        { label: 'HEURE', w: 20, align: 'center' },
        { label: 'STATUT', w: 25, align: 'center' },
        { label: 'PAIEMENT', w: 25, align: 'center' },
      ]
      const totalW = cols.reduce((s, c) => s + c.w, 0)
      const scale = cw / totalW
      cols.forEach(c => { c.w = Math.round(c.w * scale) })
      const actualW = cols.reduce((s, c) => s + c.w, 0)
      cols[0].w += (cw - actualW)
      let cx = ml
      cols.forEach(c => { c.x = cx; cx += c.w })
      const rowH = 6.5

      function drawHeader() {
        doc.setFillColor(25, 65, 115)
        doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)
        cols.forEach(c => {
          const tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          doc.text(c.label, tx, y + 4.5, { align: c.align })
        })
        y += rowH + 1
      }

      drawHeader()
      const self = this
      this.filteredResults.forEach(function (item, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(220, 225, 230); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 41, 59)

        const vals = [
          item.phone || '',
          self.formatCurrency(parseFloat(item.amount) + parseFloat(item.fee_amount)),
          self.formatCurrency(item.fee_amount),
          self.formatCurrency(item.amount),
          new Date(item.created_at).toLocaleDateString('fr-FR'),
          new Date(item.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
          self.getStatusText(item.status),
          self.getStatusText(item.partner_status),
        ]
        cols.forEach((c, i) => {
          const tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          doc.text(vals[i], tx, y + 4.2, { align: c.align })
        })
        y += rowH
      })

      // Totals
      y += 1
      doc.setFillColor(248, 250, 252); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setDrawColor(25, 65, 115); doc.setLineWidth(0.4); doc.line(ml, y, mr, y)
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59)
      doc.text('TOTAL (' + this.filteredResults.length + ' demandes)', ml + 3, y + 4.8)
      doc.text(this.formatCurrency(this.totalAmount), cols[1].x + cols[1].w - 3, y + 4.8, { align: 'right' })
      doc.text(this.formatCurrency(this.totalFees), cols[2].x + cols[2].w - 3, y + 4.8, { align: 'right' })
      doc.text(this.formatCurrency(this.totalReceive), cols[3].x + cols[3].w - 3, y + 4.8, { align: 'right' })

      // Footer
      const totalPages = doc.internal.getNumberOfPages()
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p)
        doc.setDrawColor(220, 225, 230); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(25, 65, 115)
        doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139)
        doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        const now = new Date()
        doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('retraits_natcash.pdf')
    },
    exportExcel() {
      const XLSX = require('xlsx')
      const biz = JSON.parse(localStorage.business || '{}')
      const wsData = []
      wsData.push(['RETRAITS NATCASH'])
      wsData.push([biz.name || ''])
      wsData.push([new Date().toLocaleDateString('fr-FR')])
      wsData.push([])
      wsData.push(['Telephone', 'Montant (HTG)', 'Frais (HTG)', 'A Recevoir (HTG)', 'Date', 'Heure', 'Statut', 'Paiement'])

      this.filteredResults.forEach(item => {
        wsData.push([
          item.phone || '',
          Math.round((parseFloat(item.amount) + parseFloat(item.fee_amount)) * 100) / 100,
          Math.round(parseFloat(item.fee_amount) * 100) / 100,
          Math.round(parseFloat(item.amount) * 100) / 100,
          new Date(item.created_at).toLocaleDateString('fr-FR'),
          new Date(item.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
          this.getStatusText(item.status),
          this.getStatusText(item.partner_status),
        ])
      })
      wsData.push([])
      wsData.push([
        'TOTAL (' + this.filteredResults.length + ' demandes)',
        Math.round(this.totalAmount * 100) / 100,
        Math.round(this.totalFees * 100) / 100,
        Math.round(this.totalReceive * 100) / 100,
      ])

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 14 }]
      XLSX.utils.book_append_sheet(wb, ws, 'Retraits Natcash')
      XLSX.writeFile(wb, 'retraits_natcash.xlsx')
    },
  },
  mounted() {
    this.load()
  },
}
</script>

<style scoped>
.filter-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}
.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  overflow: hidden;
}
.table-responsive {
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
}
.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}
.report-table thead th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  padding: 12px;
  text-align: center;
  border-bottom: 2px solid #e8ecf1;
  white-space: nowrap;
  z-index: 1;
}
.report-table .totals-row th {
  background: #f1f5f9;
  color: #1a2332;
  font-weight: 700;
  font-size: 0.75rem;
  border-bottom: 2px solid #cbd5e1;
}
.report-table tbody td {
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}
.report-table tbody tr:hover {
  background-color: rgba(25, 65, 115, 0.03);
}
.gap-2 { gap: 4px; }
</style>
